import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CloudSyncRounded from "@mui/icons-material/CloudSyncRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import UploadDropzone from "../components/UploadDropzone";
import { colors } from "../constants/designTokens";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import {
  getContabilidadeApiErrorDetails,
  getContabilidadeApiErrorMessage,
} from "../services/agenteContabilidadeApiService";

const LOCAL_ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function defaultStartDate() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 3);
  return toDateInputValue(date);
}

function defaultEndDate() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return toDateInputValue(date);
}

function getImportDateTimeValue(value) {
  if (!value) return 0;
  const match = String(value).match(LOCAL_ISO_DATE_TIME);
  if (match) {
    return Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isSiengeNoMapDataMessage(message, details = []) {
  const normalized = normalizeSearchText([message, ...details].join(" "));
  return (
    normalized.includes("sienge nao retornou dados do mapa") ||
    normalized.includes("nenhum dado foi retornado pelo sienge")
  );
}

function shouldIgnoreDialogClose(reason) {
  return reason === "backdropClick" || reason === "escapeKeyDown";
}

export default function ImportacaoPage() {
  const { state, actions } = useAgenteContabilidadeStore();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [autoOpenKey, setAutoOpenKey] = useState(0);
  const [siengeModalOpen, setSiengeModalOpen] = useState(false);
  const [siengeLoading, setSiengeLoading] = useState(false);
  const [siengeCostCentersLoading, setSiengeCostCentersLoading] = useState(false);
  const [siengeCostCenters, setSiengeCostCenters] = useState([]);
  const [selectedCostCenters, setSelectedCostCenters] = useState([]);
  const [siengeStartDate, setSiengeStartDate] = useState(defaultStartDate);
  const [siengeEndDate, setSiengeEndDate] = useState(defaultEndDate);
  const [siengeMessage, setSiengeMessage] = useState(null);
  const [siengeSearch, setSiengeSearch] = useState("");
  const [siengeOnlyWithCnpj, setSiengeOnlyWithCnpj] = useState(false);
  const importacoesBackend = useMemo(
    () => state.backendCatalog?.importacoes || [],
    [state.backendCatalog?.importacoes]
  );
  const empreendimentosBackend = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  const importacoesOrdenadas = useMemo(
    () =>
      [...importacoesBackend].sort((a, b) => {
        const dateA = getImportDateTimeValue(a.dataHoraCadastro || a.importedAt);
        const dateB = getImportDateTimeValue(b.dataHoraCadastro || b.importedAt);
        return dateB - dateA;
      }),
    [importacoesBackend]
  );

  const siengeFilteredCostCenters = useMemo(() => {
    const search = normalizeSearchText(siengeSearch);
    return siengeCostCenters.filter((item) => {
      if (siengeOnlyWithCnpj && !item.cnpj) {
        return false;
      }
      if (!search) {
        return true;
      }

      return [
        item.nome,
        item.nomeObraPortal,
        item.nomeEmpresaPortal,
        item.cnpj,
        item.id,
        item.idEmpresaSienge,
      ].some((value) => normalizeSearchText(value).includes(search));
    });
  }, [siengeCostCenters, siengeOnlyWithCnpj, siengeSearch]);

  function handleImportComplete(fileName, records, _parsed, file) {
    return actions.saveImportedFile(fileName, records, file);
  }

  function handleOpenImportModal() {
    setImportModalOpen(true);
    setAutoOpenKey((current) => current + 1);
  }

  function handleImportModalClose(_event, reason) {
    if (shouldIgnoreDialogClose(reason)) {
      return;
    }

    setImportModalOpen(false);
  }

  function handleSiengeModalClose(_event, reason) {
    if (shouldIgnoreDialogClose(reason) || siengeLoading) {
      return;
    }

    setSiengeModalOpen(false);
  }

  async function carregarCentrosSienge({ forceRefresh = false, resetFilters = false } = {}) {
    setSiengeCostCentersLoading(true);
    if (resetFilters) {
      setSiengeSearch("");
      setSiengeOnlyWithCnpj(false);
    }

    try {
      const centros = await actions.listSiengeCostCenters({ forceRefresh });
      const normalized = Array.isArray(centros) ? centros : [];
      setSiengeCostCenters(normalized);
      setSelectedCostCenters([]);
      if (forceRefresh) {
        setSiengeMessage({
          severity: normalized.length ? "success" : "info",
          text: normalized.length
            ? "Lista de empreendimentos atualizada."
            : "Nenhum empreendimento foi retornado pelo Sienge. Se o problema continuar, acione o suporte.",
        });
      }
    } catch (error) {
      setSiengeMessage({
        severity: "error",
        text: getContabilidadeApiErrorMessage(
          error,
          "Nao foi possivel atualizar os empreendimentos do Sienge. Tente novamente ou acione o suporte."
        ),
        details: getContabilidadeApiErrorDetails(error),
      });
    } finally {
      setSiengeCostCentersLoading(false);
    }
  }

  async function handleOpenSiengeModal() {
    setSiengeModalOpen(true);
    setSiengeMessage(null);
    await carregarCentrosSienge({ resetFilters: true });
  }

  function selectCostCenter(id) {
    setSelectedCostCenters(id ? [id] : []);
  }

  async function handleImportSienge() {
    setSiengeLoading(true);
    setSiengeMessage(null);

    try {
      const result = await actions.importFromSienge({
        costCentersId: selectedCostCenters,
        startDate: siengeStartDate,
        endDate: siengeEndDate,
      });
      const backendResult = result.backendResult || {};
      const importedCount = Number(result.newRecords || 0) + Number(result.updatedRecords || 0);
      if (backendResult.sucesso === false || Number(result.totalRecords || 0) === 0 || importedCount === 0) {
        setSiengeModalOpen(false);
        setSiengeMessage({
          severity: "warning",
          text:
            backendResult.mensagem ||
            "Nenhum dado foi importado pelo Sienge para os filtros selecionados.",
          details: Array.isArray(backendResult.erros) && backendResult.erros.length
            ? backendResult.erros
            : [
                "Revise o periodo selecionado.",
                "Confirme se os empreendimentos escolhidos possuem dados no Sienge.",
                "Se o problema continuar, acione o suporte.",
              ],
        });
        return;
      }

      setSiengeMessage({
        severity: "success",
        text:
          backendResult.mensagem ||
          `Importacao via Sienge concluida: ${result.totalRecords} registro(s), ${result.newRecords} novo(s) e ${result.updatedRecords} atualizado(s).`,
      });
      setSiengeModalOpen(false);
    } catch (error) {
      const errorMessage = getContabilidadeApiErrorMessage(
        error,
        "Nao foi possivel importar via Sienge agora."
      );
      const errorDetails = getContabilidadeApiErrorDetails(error);
      const noMapData = isSiengeNoMapDataMessage(errorMessage, errorDetails);
      if (noMapData) {
        setSiengeModalOpen(false);
      }
      setSiengeMessage({
        severity: noMapData ? "warning" : "error",
        text: noMapData
          ? "O Sienge nao retornou dados do MAPA para os empreendimentos e periodo selecionados."
          : errorMessage,
        details: noMapData && !errorDetails.length
          ? [
              "Revise o periodo selecionado.",
              "Confirme se os empreendimentos escolhidos possuem MAPA no Sienge.",
              "Tente novamente.",
              "Se o problema continuar, acione o suporte.",
            ]
          : errorDetails,
      });
    } finally {
      setSiengeLoading(false);
    }
  }

  function formatImportDate(value) {
    if (!value) return "";
    const match = String(value).match(LOCAL_ISO_DATE_TIME);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}, ${match[4]}:${match[5]}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  }

  return (
    <AgenteContabilidadeShell
      title="Importação de Dados"
      actions={[
        <Button
          key="import"
          variant="contained"
          startIcon={<UploadFileOutlined />}
          onClick={handleOpenImportModal}
          sx={{
            height: 40,
            borderRadius: 2.2,
            px: 2.4,
            textTransform: "none",
            fontWeight: 900,
          }}
        >
          Importar arquivo
        </Button>,
        <Button
          key="sienge"
          variant="outlined"
          startIcon={
            siengeLoading ? <CircularProgress color="inherit" size={18} /> : <CloudSyncRounded />
          }
          onClick={handleOpenSiengeModal}
          disabled={siengeLoading}
          sx={{
            height: 40,
            borderRadius: 2.2,
            px: 2.4,
            textTransform: "none",
            fontWeight: 900,
            bgcolor: colors.surface,
          }}
        >
          {siengeLoading ? "Importando..." : "Importar via Sienge"}
        </Button>,
      ]}
    >
      {siengeMessage ? (
        <Alert severity={siengeMessage.severity} variant="outlined" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 14 }}>{siengeMessage.text}</Typography>
          {Array.isArray(siengeMessage.details) && siengeMessage.details.length ? (
            <Stack component="ul" spacing={0.4} sx={{ m: 0, mt: 0.8, pl: 2.2 }}>
              {siengeMessage.details.map((detail) => (
                <Typography component="li" key={detail} sx={{ fontSize: 12.5 }}>
                  {detail}
                </Typography>
              ))}
            </Stack>
          ) : null}
        </Alert>
      ) : null}

      <Dialog
        open={importModalOpen}
        onClose={handleImportModalClose}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: colors.surface,
          },
        }}
      >
        <DialogTitle sx={{ px: 2.4, py: 1.8, pr: 6 }}>
          <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 20 }}>
            Importação de Dados
          </Typography>
          <IconButton
            aria-label="Fechar importacao"
            onClick={() => setImportModalOpen(false)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.4, pb: 2.4, pt: 0 }}>
          <UploadDropzone
            compact
            autoOpenKey={autoOpenKey}
            onImportComplete={handleImportComplete}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={siengeModalOpen}
        onClose={handleSiengeModalClose}
        disableEscapeKeyDown
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: colors.surface,
          },
        }}
      >
        <DialogTitle sx={{ px: 2.4, py: 1.8, pr: 6 }}>
          <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 20 }}>
            Importar via Sienge
          </Typography>
          <IconButton
            aria-label="Fechar importacao via Sienge"
            onClick={() => setSiengeModalOpen(false)}
            disabled={siengeLoading}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.4, pb: 1, pt: 1 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ pt: 0.8 }}>
              <TextField
                label="Data inicial"
                type="date"
                value={siengeStartDate}
                onChange={(event) => setSiengeStartDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  mt: 0.5,
                  "& .MuiInputLabel-root": {
                    bgcolor: colors.surface,
                    px: 0.5,
                  },
                }}
              />
              <TextField
                label="Data final"
                type="date"
                value={siengeEndDate}
                onChange={(event) => setSiengeEndDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  mt: 0.5,
                  "& .MuiInputLabel-root": {
                    bgcolor: colors.surface,
                    px: 0.5,
                  },
                }}
              />
            </Stack>

            <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ px: 1.5, py: 1.2, bgcolor: colors.surfaceAlt, borderBottom: `1px solid ${colors.border}` }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                      Centros de custo
                    </Typography>
                    <Typography sx={{ color: colors.softMuted, fontSize: 12.5 }}>
                      Selecione os centros que serao consultados no MAPA Imobiliario.
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mt: 1.2 }}
                >
                  <TextField
                    value={siengeSearch}
                    onChange={(event) => setSiengeSearch(event.target.value)}
                    placeholder="Pesquisar empreendimento"
                    size="small"
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      px: 1,
                      py: 0.3,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 1.2,
                      bgcolor: colors.surface,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Checkbox
                      checked={siengeOnlyWithCnpj}
                      onChange={(event) => setSiengeOnlyWithCnpj(event.target.checked)}
                      size="small"
                    />
                    <Typography sx={{ color: colors.text, fontSize: 12.5, fontWeight: 800 }}>
                      Somente com CNPJ
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {siengeCostCentersLoading ? (
                <Stack direction="row" alignItems="center" spacing={1.2} sx={{ p: 2 }}>
                  <CircularProgress size={18} />
                  <Typography sx={{ color: colors.softMuted, fontSize: 13 }}>
                    Carregando centros de custo...
                  </Typography>
                </Stack>
              ) : siengeFilteredCostCenters.length ? (
                <Stack sx={{ maxHeight: 340, overflow: "auto" }}>
                  {siengeFilteredCostCenters.map((item) => {
                    const id = item.id;
                    const checked = selectedCostCenters[0] === id;
                    const exists = item.jaExisteNoPortal !== false;

                    return (
                      <Stack
                        key={id || `${item.idEmpresaSienge}-${item.nome}`}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ px: 1.2, py: 1, borderBottom: `1px solid ${colors.border}` }}
                      >
                        <Radio
                          checked={checked}
                          disabled={!id || siengeLoading}
                          onChange={() => selectCostCenter(id)}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: colors.text, fontWeight: 800, wordBreak: "break-word" }}>
                            {item.nome || `Centro ${id}`}
                          </Typography>
                          <Typography sx={{ color: colors.softMuted, fontSize: 12.5 }}>
                            Centro {id || "-"} · Empresa Sienge {item.idEmpresaSienge || "-"}
                            {item.cnpj ? ` · ${item.cnpj}` : ""}
                          </Typography>
                        </Box>
                        <Chip
                          label={exists ? "Existe no portal" : "Pendente no portal"}
                          size="small"
                          sx={{
                            bgcolor: exists ? "#d8f9e9" : "#fff7df",
                            color: exists ? "#08765c" : "#9a6700",
                            fontWeight: 800,
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="info" variant="outlined" sx={{ m: 1.5 }}>
                  Nenhum centro de custo encontrado para os filtros selecionados.
                </Alert>
              )}
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.4, pb: 2.2 }}>
          <Button
            onClick={() => setSiengeModalOpen(false)}
            disabled={siengeLoading}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={siengeLoading ? <CircularProgress color="inherit" size={18} /> : <CloudSyncRounded />}
            onClick={handleImportSienge}
            disabled={siengeLoading || !selectedCostCenters.length || !siengeStartDate || !siengeEndDate}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {siengeLoading ? "Importando..." : "Importar selecionados"}
          </Button>
        </DialogActions>
      </Dialog>

      {importacoesOrdenadas.length ? (
        <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: 2.4, bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.2} sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 28, height: 28, borderRadius: 2, bgcolor: colors.primary, color: "#fff", display: "grid", placeItems: "center" }}>
                <HistoryRounded sx={{ fontSize: 17 }} />
              </Box>
              <Box>
                <Typography sx={{ color: colors.text, fontWeight: 900 }}>
                  Historico de Importacoes
                </Typography>
                <Typography sx={{ color: colors.softMuted, fontSize: 12.5 }}>
                  {empreendimentosBackend.length
                    ? `${empreendimentosBackend.length} empreendimento(s) retornado(s) pela API`
                    : "Dados carregados da API quando disponiveis"}
                </Typography>
              </Box>
            </Stack>
            <Chip label="API" size="small" sx={{ bgcolor: "#d8f9e9", color: "#08765c", fontWeight: 800 }} />
          </Stack>

          <Stack spacing={1}>
            {importacoesOrdenadas.slice(0, 6).map((item) => {
              const id = item.idImportacao ?? item.id;
              const totalRegistros = item.totalRegistros ?? item.totalRecords ?? 0;
              const novosRegistros = item.novosRegistros ?? item.newRecords ?? 0;
              const registrosAtualizados = item.registrosAtualizados ?? item.updatedRecords ?? 0;
              const registrosIgnorados = item.registrosIgnorados ?? item.unchangedRecords ?? 0;
              const nomeArquivo = item.nomeArquivo ?? item.fileName ?? "Arquivo importado";
              const dataCadastro = item.dataHoraCadastro ?? item.importedAt;
              const status = item.status ?? (item.noChanges ? "SEM ALTERACAO" : "CONCLUIDA");

              return (
                <Paper key={id || `${nomeArquivo}-${dataCadastro}`} elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: colors.text, fontWeight: 800, wordBreak: "break-word" }}>
                        {nomeArquivo}
                      </Typography>
                      <Typography sx={{ color: colors.softMuted, fontSize: 12.5 }}>
                        {formatImportDate(dataCadastro)}
                        {item.mensagem || item.message ? ` · ${item.mensagem || item.message}` : ""}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                      <Chip label={status} size="small" sx={{ bgcolor: "#eef4ff", color: colors.primary, fontWeight: 800 }} />
                      <Chip label={`${totalRegistros} registro(s)`} size="small" sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, fontWeight: 700 }} />
                      <Chip label={`${novosRegistros} novo(s)`} size="small" sx={{ bgcolor: "#d8f9e9", color: "#08765c", fontWeight: 700 }} />
                      <Chip label={`${registrosAtualizados} atualizado(s)`} size="small" sx={{ bgcolor: "#eaf3ff", color: "#0054ff", fontWeight: 700 }} />
                      {registrosIgnorados ? (
                        <Chip label={`${registrosIgnorados} ignorado(s)`} size="small" sx={{ bgcolor: "#fff7df", color: "#9a6700", fontWeight: 700 }} />
                      ) : null}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
          Nenhuma importacao retornada pela API ate o momento.
        </Alert>
      )}

    </AgenteContabilidadeShell>
  );
}
